import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { ControllerInputMessage, GameRoom, PlayerSlot } from './api.types';

const apiBaseUrl = ['localhost', '127.0.0.1'].includes(window.location.hostname)
  ? 'http://localhost:5024'
  : window.location.origin;

@Injectable({ providedIn: 'root' })
export class GameApiService {
  readonly connectionState = signal<'idle' | 'connecting' | 'connected' | 'error'>('idle');

  private connection?: signalR.HubConnection;
  private sequence = 0;

  constructor(private readonly http: HttpClient) {}

  createRoom(hostName: string) {
    return this.http.post<GameRoom>(`${apiBaseUrl}/api/rooms`, { hostName });
  }

  joinRoom(roomCode: string, playerName: string) {
    return this.http.post<PlayerSlot>(`${apiBaseUrl}/api/rooms/${roomCode}/players`, { playerName });
  }

  async connectController(roomCode: string, player: PlayerSlot): Promise<void> {
    this.connectionState.set('connecting');

    this.connection = this.createConnection();

    try {
      await this.connection.start();
      await this.connection.invoke('JoinController', roomCode, player.playerId, player.playerName);
      this.connectionState.set('connected');
    } catch {
      this.connectionState.set('error');
      throw new Error('Could not connect to the game room.');
    }
  }

  async sendInput(roomCode: string, playerId: string, input: string, value: number): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    const message: ControllerInputMessage = {
      roomCode,
      playerId,
      input,
      value,
      sequence: ++this.sequence,
      sentAt: new Date().toISOString()
    };

    await this.connection.invoke('SendInput', message);
  }

  async connectHost(
    roomCode: string,
    onControllerInput: (message: ControllerInputMessage) => void
  ): Promise<void> {
    this.connectionState.set('connecting');

    this.connection = this.createConnection();
    this.connection.on('ControllerInput', onControllerInput);

    try {
      await this.connection.start();
      await this.connection.invoke('JoinHost', roomCode);
      this.connectionState.set('connected');
    } catch {
      this.connectionState.set('error');
      throw new Error('Could not connect to the host screen.');
    }
  }

  async disconnect(): Promise<void> {
    if (!this.connection) {
      return;
    }

    await this.connection.stop();
    this.connection = undefined;
    this.connectionState.set('idle');
  }

  private createConnection(): signalR.HubConnection {
    return new signalR.HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/hubs/game-room`)
      .withAutomaticReconnect()
      .build();
  }
}
